import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Filters, UnifiedSearchTab } from '../constants'

interface SearchParamsState {
  searchTerm: string
  selectedTab: UnifiedSearchTab
  filters: Filters
  sortBy: string
  firstBitePage: number
  unmatchedPage: number
  firstBiteDoorPage: number
  unmatchedDoorPage: number
  variantPage: number
  dealPage: number
}

interface UseUnifiedSearchParamsReturn {
  searchParamsState: SearchParamsState
  updateSearchParams: (updates: Partial<SearchParamsState>) => void
  clearSearchParams: () => void
}

export function useUnifiedSearchParams(): UseUnifiedSearchParamsReturn {
  const [searchParams, setSearchParams] = useSearchParams()

  // Helper function to update URL parameters
  const updateSearchParams = useCallback(
    (updates: Partial<SearchParamsState>) => {
      const newSearchParams = new URLSearchParams(searchParams)

      // Handle each possible update
      if (updates.searchTerm !== undefined) {
        if (updates.searchTerm === '') {
          newSearchParams.delete('q')
        } else {
          newSearchParams.set('q', updates.searchTerm)
        }
      }

      if (updates.selectedTab !== undefined) {
        if (updates.selectedTab === 'firstbite') {
          newSearchParams.delete('tab')
        } else {
          newSearchParams.set('tab', updates.selectedTab)
        }
      }

      if (updates.filters !== undefined) {
        const { category, type, product } = updates.filters

        if (category === 'all') {
          newSearchParams.delete('category')
        } else {
          newSearchParams.set('category', category)
        }

        if (type === 'all') {
          newSearchParams.delete('type')
        } else {
          newSearchParams.set('type', type)
        }

        if (!product || product === 'all') {
          newSearchParams.delete('product')
        } else {
          newSearchParams.set('product', product)
        }
      }

      if (updates.sortBy !== undefined) {
        if (updates.sortBy === 'relevance') {
          newSearchParams.delete('sort')
        } else {
          newSearchParams.set('sort', updates.sortBy)
        }
      }

      if (updates.firstBitePage !== undefined) {
        if (updates.firstBitePage === 1) {
          newSearchParams.delete('fbPage')
        } else {
          newSearchParams.set('fbPage', updates.firstBitePage.toString())
        }
      }

      if (updates.unmatchedPage !== undefined) {
        if (updates.unmatchedPage === 1) {
          newSearchParams.delete('umPage')
        } else {
          newSearchParams.set('umPage', updates.unmatchedPage.toString())
        }
      }

      if (updates.firstBiteDoorPage !== undefined) {
        if (updates.firstBiteDoorPage === 1) {
          newSearchParams.delete('fbDoorPage')
        } else {
          newSearchParams.set(
            'fbDoorPage',
            updates.firstBiteDoorPage.toString()
          )
        }
      }

      if (updates.unmatchedDoorPage !== undefined) {
        if (updates.unmatchedDoorPage === 1) {
          newSearchParams.delete('umDoorPage')
        } else {
          newSearchParams.set(
            'umDoorPage',
            updates.unmatchedDoorPage.toString()
          )
        }
      }

      if (updates.variantPage !== undefined) {
        if (updates.variantPage === 1) {
          newSearchParams.delete('varPage')
        } else {
          newSearchParams.set('varPage', updates.variantPage.toString())
        }
      }

      if (updates.dealPage !== undefined) {
        if (updates.dealPage === 1) {
          newSearchParams.delete('dealPage')
        } else {
          newSearchParams.set('dealPage', updates.dealPage.toString())
        }
      }

      setSearchParams(newSearchParams)
    },
    [searchParams, setSearchParams]
  )

  // Initialize state from URL parameters
  const searchParamsState = useMemo((): SearchParamsState => {
    return {
      searchTerm: searchParams.get('q') || '',
      selectedTab: (searchParams.get('tab') as UnifiedSearchTab) || 'firstbite',
      filters: {
        category: searchParams.get('category') || 'all',
        type: searchParams.get('type') || 'all',
        product: searchParams.get('product') || 'all',
      },
      sortBy: searchParams.get('sort') || 'relevance',
      firstBitePage: parseInt(searchParams.get('fbPage') || '1'),
      unmatchedPage: parseInt(searchParams.get('umPage') || '1'),
      firstBiteDoorPage: parseInt(searchParams.get('fbDoorPage') || '1'),
      unmatchedDoorPage: parseInt(searchParams.get('umDoorPage') || '1'),
      variantPage: parseInt(searchParams.get('varPage') || '1'),
      dealPage: parseInt(searchParams.get('dealPage') || '1'),
    }
  }, [searchParams])

  // Clear all search-related params
  const clearSearchParams = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.delete('search')
    newSearchParams.delete('q')
    newSearchParams.delete('tab')
    newSearchParams.delete('category')
    newSearchParams.delete('type')
    newSearchParams.delete('product')
    newSearchParams.delete('sort')
    newSearchParams.delete('fbPage')
    newSearchParams.delete('umPage')
    newSearchParams.delete('fbDoorPage')
    newSearchParams.delete('umDoorPage')
    newSearchParams.delete('varPage')
    newSearchParams.delete('dealPage')
    setSearchParams(newSearchParams)
  }, [searchParams, setSearchParams])

  return {
    searchParamsState,
    updateSearchParams,
    clearSearchParams,
  }
}
